export type EntityId = string;

export type Effect =
  | {
      kind: "toast";
      message: string;
      level: "success" | "info" | "warning" | "error";
      ms?: number;
    }
  | { kind: "sound"; sound: "impact" | "pickup"; amount?: number }
  | { kind: "storage.set"; key: string; value: string }
  | { kind: "storage.remove"; key: string }
  | { kind: "ws.send"; payload: any }
  | { kind: "bullets.recompute" }
  | { kind: "ui.topbar"; show: boolean }
  | { kind: "ui.joinModal"; show: boolean }
  | { kind: "ui.buttons"; inLobby: boolean }
  | { kind: "ui.resizeCanvas" }
  | { kind: "ui.render" }
  | { kind: "ui.leaderboard.visible"; show: boolean }
  | { kind: "ui.countdown"; active: boolean; seconds?: number; text?: string }
  | {
      kind: "ui.playersPills";
      names: Array<{ id: string; name: string; self: boolean }>;
    }
  | { kind: "ui.gameStats"; show: boolean }
  | { kind: "ui.perfStats"; show: boolean }
  | {
      kind: "ui.deathModal";
      show: boolean;
      title?: string;
      titleClass?: string;
      message?: string;
    }
  | {
      kind: "ui.deathStats";
      stats: Partial<{
        kills: number;
        deaths: number;
        pelletsCollected: number;
        maxHealth: number;
        damageMultiplier: number;
        speedMultiplier: number;
      }>;
    }
  | { kind: "ui.setGameModeState"; gameModeState: any }
  | {
      kind: "ui.showGameEnd";
      isWinner: boolean;
      isDraw: boolean;
      reason: string;
      finalScores: { [teamId: number]: number };
      playerStats: Array<{
        playerId: string;
        playerName: string;
        kills: number;
        deaths: number;
        pelletsCollected: number;
        score: number;
        teamId?: number;
      }>;
      localTeamId?: number;
      localPlayerId: string | null;
    }
  | { kind: "ui.joinButtonsReset"; enabled: boolean; label: string }
  | {
      kind: "ui.damageNumber";
      x: number;
      y: number;
      damage: number;
      source: string;
    }
  | { kind: "ui.updateLobbyDisplay"; lobbyId: string | null }
  | {
      kind: "metrics.smoothed";
      tps: number;
      frameTime: number;
      entities: any;
      ping: number;
    }
  | { kind: "screen.shake"; intensity?: number }
  | {
      kind: "screen.flash";
      intensity?: number;
      r?: number;
      g?: number;
      b?: number;
    }
  | { kind: "webgl.bloomSpike"; intensity?: number; threshold?: number }
  | { kind: "webgl.chromaticSpike"; intensity?: number }
  | {
      kind: "webgl.heatDistortion";
      x: number;
      y: number;
      intensity?: number;
      radius?: number;
    }
  | {
      kind: "webgl.gpuExplosion";
      x: number;
      y: number;
      intensity?: number;
      colorPalette?: string[];
      radius?: number;
      preset?: "small" | "medium" | "large";
    }
  | { kind: "webgl.radialBlur"; intensity?: number; x?: number; y?: number }
  | {
      kind: "particles.explosion";
      x: number;
      y: number;
      radius?: number;
      tag?: string;
    }
  | {
      kind: "particles.mineExplosion";
      x: number;
      y: number;
      radius?: number;
      tag?: string;
    }
  | { kind: "lobby.requestList" }
  | { kind: "lobby.updateList"; lobbies: any[] }
  | { kind: "react.updateState"; state: any }
  | { kind: "react.updateParty"; party: any };

export interface ComponentStores {
  players: Map<string, any>;
  playersTarget: Map<string, any>;
  asteroids: Map<string, any>;
  asteroidsTarget: Map<string, any>;
  pellets: Map<string, any>;
  pelletsTarget: Map<string, any>;
  bullets: any[];
  mines: any[];
  lobbyPeakStats: Map<
    string,
    {
      kills: number;
      deaths: number;
      pelletsCollected: number;
      maxHealth: number;
      damageMultiplier: number;
      speedMultiplier: number;
    }
  >;
}

export interface WorldDeps {
  state: any;
  getPlayerName: () => string;
  now: () => number;
  updateLobbyDisplay: (lobbyId: string | null) => void;
  updateButtons: (inLobby: boolean) => void;
  resizeCanvas: () => void;
  render: () => void;
  updateSmoothedMetrics: (metrics: any) => void;
  damageNumberManager: any;
  isVisible: (x: number, y: number, margin?: number) => boolean;
  CoordinateValidator: any;
  reconcileWithServer: (serverPlayer: any, acknowledgedSeq: number) => void;
  triggerScreenShake: (intensity?: number) => void;
  triggerScreenFlash: (
    intensity?: number,
    r?: number,
    g?: number,
    b?: number,
  ) => void;
  unifiedPostProcess: any; // UnifiedPostProcessEffects instance
  gpuExplosionParticles: any; // GPUExplosionParticles instance
  previousPelletsCollected: { value: number };
  previousHealth: { value: number };
  previousBoostAt: { value: number };
  topbarEl: HTMLElement | null;
  gameStatsEl: HTMLElement | null;
  perfStatsEl: HTMLElement | null;
  joinGameBtnEl: HTMLButtonElement | null;
  loggedInPlayBtnEl: HTMLButtonElement | null;
  setGlobalState: (state: any) => void;
}

export interface World {
  deps: WorldDeps;
  comps: ComponentStores;
  effects: Effect[];
}

export function createWorld(deps: WorldDeps): World {
  const s = deps.state;
  return {
    deps,
    effects: [],
    comps: {
      players: s.players,
      playersTarget: s.playersTarget,
      asteroids: s.asteroids,
      asteroidsTarget: s.asteroidsTarget,
      pellets: s.pellets,
      pelletsTarget: s.pelletsTarget,
      bullets: s.bullets,
      mines: s.mines,
      lobbyPeakStats: new Map(),
    },
  };
}

export function pushEffect(world: World, effect: Effect) {
  world.effects.push(effect);
}
